import { useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Plus, Settings, X } from '../../../icons';
import {
  Button, Checkbox, Combobox, EmptyState, FilterChip, FormField, Input, PageHeader, Switch, Table, Tabs, Textarea,
  UnsavedGuard, toast, type TableColumn,
} from '../../../components/shared';
import { layerConfirm } from '../../../components/LayerPopup/LayerPopup';
import {
  addBoardMember, createPrefix, deletePrefix, getBoard, patchBoard, removeBoardMember, saveBoardShares,
  type BoardDetailResponse, type BoardDto, type BoardMemberDto, type BoardPrefixDto, type ShareTargetType,
} from '../../../api/boards';
import { listAllTeams, listDivisions, type DivisionDto, type TeamDto } from '../../../api/organization';
import { searchUsers } from '../../../api/users';
import { getBoardsLang, subscribeBoardsLang, tBoards, type BoardsI18nKey } from '../../../lib/boards-i18n';
import type { TargetRow } from '../../BoardsPage/TargetPicker';
import { BoardMark } from '../BoardMarks';
import { boardListPath, parseSettingsTab, type BoardSettingsTab } from '../board-v1-url';
import {
  buildBoardPatch, changedSettings, formFromBoard, notifyTargetCount, shareKey, shareRowsFromDto, sharesFingerprint,
  toShareInputs, type SettingsForm,
  // 길이 상한은 서버가 정본이다 — 맨숫자를 박지 않는다(settings-model.ts 의 그 주석 참조).
  BOARD_NAME_MAX, BOARD_DESC_MAX, PREFIX_LABEL_MAX,
} from './settings-model';
import './board-settings.css';

/**
 * 게시판 설정 — 시안 v1 `axi-board-settings` (U5 · 2026-09-15 · 주소 `/board/:boardId/settings?tab=basic|share|post|notify`).
 *
 *   머리(경로 · 게시판 설정 · 게시판으로)
 *   [카드: 머리(타일 · 이름 · 표식 · 알림 받는 사람) · 가로 탭(바뀐 항목) · 칸 · 저장 막대]
 *
 * ★ 저장 규칙은 현행 `BoardSettingsScreen` 과 같다(`settings-model`) — 변경분만 PATCH · 공유 목록은 통째로 PUT ·
 *   말머리 · 운영자는 누르는 즉시.
 * ★ 규칙 9 — 설정은 **게시판별**: 주소의 `:boardId` 로만 읽고, 게시판이 바뀌면 폼을 `key` 로 버린다.
 * ★ 규칙 3 — 알림 사용을 끄면 알림 대상 체크는 흐리게 잠그되 **값은 지우지 않는다**(다시 켜면 되살아난다).
 * ★ 기본 회사 게시판(`systemKey`)은 삭제할 수 없다 — 잠긴 안내만.
 * ★★ 게시판 삭제는 **보류**(사용자 결정 · 서버 R14 — 글 · 첨부를 휴지통 30일로 보내는 동작) — 단추를 두지 않고 안내만.
 * ★★ 서버 짝이라 「아직 없음」: 알림 받는 사람 · 공유 인원(R6) · 알림 미리보기(R5) · 최근 보낸 알림(R4).
 * ★ 공유 · 권한 탭의 작성 허용은 서버 R15(관리 콘솔 PUT shares 가 canWrite 를 되돌림) 결함과 맞물린다 — PR 선행 조건.
 */

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

type Loaded =
  | { status: 'loading' }
  | { status: 'ready'; detail: BoardDetailResponse }
  | { status: 'missing' }
  | { status: 'error'; message: string };

export function BoardSettingsV1Screen({ boardId }: { boardId: string }) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const [sp] = useSearchParams();
  const tab = parseSettingsTab(sp);
  const [loaded, setLoaded] = useState<Loaded>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoaded({ status: 'loading' });
    getBoard(boardId)
      .then((detail) => { if (!cancelled) setLoaded({ status: 'ready', detail }); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as ApiError)?.response?.status;
        setLoaded(status === 404 || status === 403 ? { status: 'missing' } : { status: 'error', message: errMsg(e) });
      });
    return () => { cancelled = true; };
  }, [boardId, attempt]);

  const title = tBoards('boards.v1.settings.title');
  const crumb = tBoards('boards.v1.crumb');
  const root = (children: ReactNode) => (
    <div className="bv1-page bv1-settings" data-board-screen="settings" data-id={boardId} data-tab={tab}>{children}</div>
  );
  const plainHead = (boardName?: string) => (
    <PageHeader className="bv1-head" breadcrumb={[crumb, boardName ?? crumb, title]} title={title} />
  );

  if (loaded.status === 'loading') {
    return root(<>{plainHead()}<section className="bv1-card"><div className="bv1-loading" aria-busy="true">{tBoards('boards.v1.loading')}</div></section></>);
  }
  if (loaded.status === 'missing') {
    return root(<>{plainHead()}<section className="bv1-card"><EmptyState kind="no-permission" icon={Lock} title={tBoards('boards.v1.write.board_missing')} /></section></>);
  }
  if (loaded.status === 'error') {
    return root(
      <>
        {plainHead()}
        <section className="bv1-card">
          <EmptyState
            kind="error"
            title={tBoards('boards.v1.load_failed')}
            description={loaded.message}
            action={<Button variant="secondary" size="compact" onClick={() => setAttempt((n) => n + 1)}>{tBoards('boards.v1.retry')}</Button>}
          />
        </section>
      </>,
    );
  }
  const { detail } = loaded;
  if (detail.board.canManage !== true) {
    return root(
      <>
        {plainHead(detail.board.name)}
        <section className="bv1-card">
          <EmptyState kind="no-permission" icon={Lock} title={tBoards('boards.v1.settings.deny.title')} description={tBoards('boards.v1.settings.deny.desc')} />
        </section>
      </>,
    );
  }
  return root(<SettingsFormView key={detail.board.id} detail={detail} tab={tab} />);
}

/** 사용자 찾기 — 글자를 멈추고 250ms 뒤에 부른다(현행 `TargetPicker` 와 같음). */
function useUserSearch(query: string, enabled = true) {
  const [options, setOptions] = useState<Array<{ id: string; name: string; email?: string | null }>>([]);
  useEffect(() => {
    const q = query.trim();
    if (!enabled || q.length === 0) { setOptions([]); return; }
    let cancelled = false;
    const handle = setTimeout(() => {
      searchUsers(q, 10)
        .then((rows) => { if (!cancelled) setOptions(rows.map((u) => ({ id: String(u.id), name: u.name, email: (u as { email?: string | null }).email ?? null }))); })
        .catch(() => undefined);
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [query, enabled]);
  return options;
}

function OptRow({ title, hint, off, children }: { title: string; hint?: ReactNode; off?: boolean; children?: ReactNode }) {
  return (
    <div className={`bv1-opt${off ? ' is-off' : ''}`}>
      <div className="bv1-opt__text"><b>{title}</b>{hint != null && <span>{hint}</span>}</div>
      {children}
    </div>
  );
}

const KIND_KEY: Record<ShareTargetType, BoardsI18nKey> = {
  company: 'boards.target.company',
  division: 'boards.target.division',
  team: 'boards.target.team',
  user: 'boards.target.user',
};

function SettingsFormView({ detail, tab }: { detail: BoardDetailResponse; tab: BoardSettingsTab }) {
  const navigate = useNavigate();
  const [, setSp] = useSearchParams();

  // 저장된 기준 — 저장이 끝나면 새 기준이 된다(「바뀐 항목」이 0 으로 돌아간다).
  const [board, setBoard] = useState<BoardDto>(detail.board);
  const [form, setForm] = useState<SettingsForm>(() => formFromBoard(detail.board));
  const set = <K extends keyof SettingsForm>(key: K, value: SettingsForm[K]) => setForm((f) => ({ ...f, [key]: value }));
  const [shares, setShares] = useState<TargetRow[]>(() => shareRowsFromDto(detail.shares));
  const [baseShares, setBaseShares] = useState(() => sharesFingerprint(shareRowsFromDto(detail.shares)));
  const [prefixes, setPrefixes] = useState<BoardPrefixDto[]>(detail.prefixes);
  const [members, setMembers] = useState<BoardMemberDto[]>(detail.members);

  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [teams, setTeams] = useState<Array<TeamDto & { divisionName: string | null }>>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>(
    () => Object.fromEntries(detail.members.filter((m) => m.name).map((m) => [m.userId, m.name as string])),
  );

  const [kind, setKind] = useState<ShareTargetType>('team');
  const [pick, setPick] = useState('');
  const [shareQuery, setShareQuery] = useState('');
  const shareUsers = useUserSearch(shareQuery, kind === 'user');
  const [managerQuery, setManagerQuery] = useState('');
  const managerUsers = useUserSearch(managerQuery);
  const [managerPick, setManagerPick] = useState('');
  const [newPrefix, setNewPrefix] = useState('');

  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listDivisions(), listAllTeams()])
      .then(([d, t]) => { if (!cancelled) { setDivisions(d); setTeams(t); } })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  const sharesDirty = sharesFingerprint(shares) !== baseShares;
  const changed = changedSettings(board, form, sharesDirty);
  const canSave = changed.length > 0 && form.name.trim().length > 0 && !saving;

  const nameOf = (t: TargetRow): string => {
    if (t.targetType === 'company') return tBoards('boards.target.company');
    if (t.targetType === 'division') return divisions.find((d) => d.id === t.targetId)?.name ?? tBoards('boards.v1.settings.share.unknown');
    if (t.targetType === 'team') {
      const team = teams.find((x) => x.id === t.targetId);
      return team ? (team.divisionName ? `${team.divisionName} · ${team.name}` : team.name) : tBoards('boards.v1.settings.share.unknown');
    }
    return (t.targetId && userNames[t.targetId]) || tBoards('boards.v1.settings.share.unknown');
  };

  const pickOptions = useMemo(() => {
    if (kind === 'division') return divisions.map((d) => ({ value: d.id, label: d.name }));
    if (kind === 'team') return teams.map((t) => ({ value: t.id, label: t.divisionName ? `${t.divisionName} · ${t.name}` : t.name }));
    if (kind === 'user') return shareUsers.map((u) => ({ value: u.id, label: u.name }));
    return [];
  }, [kind, divisions, teams, shareUsers]);

  const setTab = (next: string) => setSp(new URLSearchParams({ tab: next }), { replace: true });

  function addShare() {
    if (kind !== 'company' && !pick) return;
    const row: TargetRow = { targetType: kind, targetId: kind === 'company' ? '0' : pick, canRead: true, canNotify: false, canWrite: false };
    if (shares.some((s) => shareKey(s) === shareKey(row))) return;
    if (kind === 'user') {
      const u = shareUsers.find((x) => x.id === pick);
      if (u) setUserNames((m) => ({ ...m, [u.id]: u.name }));
    }
    setShares([...shares, row]);
    setPick('');
    setShareQuery('');
  }
  const patchShare = (t: TargetRow, patch: Partial<TargetRow>) =>
    setShares((rows) => rows.map((r) => (shareKey(r) === shareKey(t) ? { ...r, ...patch } : r)));

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const patch = buildBoardPatch(board, form);
      if (Object.keys(patch).length > 0) {
        const saved = { ...board, ...(await patchBoard(board.id, patch as Parameters<typeof patchBoard>[1])) };
        setBoard(saved);
        setForm(formFromBoard(saved));
      }
      if (sharesDirty) {
        const rows = shareRowsFromDto(await saveBoardShares(board.id, toShareInputs(shares)));
        setShares(rows);
        setBaseShares(sharesFingerprint(rows));
      }
      toast.success(tBoards('boards.v1.settings.saved'));
    } catch (e) {
      setError(tBoards('boards.settings.err_save', { msg: errMsg(e) }));
    } finally {
      setSaving(false);
    }
  }

  async function instant(action: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try { await action(); } catch (e) { setError(tBoards('boards.v1.post.err_action', { msg: errMsg(e) })); } finally { setBusy(false); }
  }

  const addPrefix = () => instant(async () => {
    const label = newPrefix.trim();
    if (!label) return;
    const created = await createPrefix(board.id, { label });
    setPrefixes((prev) => [...prev, created]);
    setNewPrefix('');
  });
  const removePrefix = (p: BoardPrefixDto) => instant(async () => {
    const ok = await layerConfirm(tBoards('boards.v1.settings.post.prefix_remove_confirm', { name: p.label }), {
      title: tBoards('boards.v1.settings.post.prefix_remove_title'), danger: true,
    });
    if (!ok) return;
    await deletePrefix(board.id, p.id);
    setPrefixes((prev) => prev.filter((x) => x.id !== p.id));
  });
  const addManager = () => instant(async () => {
    if (!managerPick) return;
    const m = await addBoardMember(board.id, { userId: managerPick, role: 'MANAGER' });
    setMembers((prev) => [...prev.filter((x) => x.userId !== m.userId), m]);
    if (m.name) setUserNames((names) => ({ ...names, [m.userId]: m.name as string }));
    setManagerPick('');
    setManagerQuery('');
  });
  const removeManager = (m: BoardMemberDto) => instant(async () => {
    await removeBoardMember(board.id, m.userId);
    setMembers((prev) => prev.filter((x) => x.userId !== m.userId));
  });

  const kindBadge = (t: TargetRow) => (
    <span className={`bv1-kbadge${t.targetType === 'team' || t.targetType === 'user' ? '' : ' is-pur'}`}>{tBoards(KIND_KEY[t.targetType])}</span>
  );

  const shareColumns: TableColumn<TargetRow>[] = [
    { key: 'target', header: tBoards('boards.v1.settings.share.col_target'), render: (t) => <b>{nameOf(t)}</b> },
    { key: 'kind', header: tBoards('boards.v1.settings.share.col_kind'), render: kindBadge },
    { key: 'count', header: tBoards('boards.v1.settings.share.col_count'), numeric: true, render: () => '—' },
    {
      key: 'read', header: tBoards('boards.v1.settings.share.col_read'), align: 'center',
      render: (t) => <Checkbox checked={t.canRead ?? true} aria-label={tBoards('boards.v1.settings.share.read_aria', { name: nameOf(t) })} onChange={(e) => patchShare(t, { canRead: e.target.checked })} />,
    },
    {
      key: 'write', header: tBoards('boards.v1.settings.share.col_write'), align: 'center',
      render: (t) => <Checkbox checked={t.canWrite ?? false} aria-label={tBoards('boards.v1.settings.share.write_aria', { name: nameOf(t) })} onChange={(e) => patchShare(t, { canWrite: e.target.checked })} />,
    },
    {
      key: 'notify', header: tBoards('boards.v1.settings.share.col_notify'), align: 'center',
      render: (t) => <Checkbox checked={t.canNotify ?? false} disabled={!form.notifyEnabled} aria-label={tBoards('boards.v1.settings.share.notify_aria', { name: nameOf(t) })} onChange={(e) => patchShare(t, { canNotify: e.target.checked })} />,
    },
    {
      key: 'remove', header: <span className="bv1-sr">{tBoards('boards.v1.settings.share.col_remove')}</span>, align: 'right', width: '56px',
      render: (t) => (
        <Button variant="ghost" size="sm" iconOnly aria-label={tBoards('boards.v1.settings.share.remove_share', { name: nameOf(t) })} onClick={() => setShares((rows) => rows.filter((r) => shareKey(r) !== shareKey(t)))}>
          <X aria-hidden />
        </Button>
      ),
    },
  ];

  const notifyColumns: TableColumn<TargetRow>[] = [
    { key: 'target', header: tBoards('boards.v1.settings.share.col_target'), render: (t) => <b>{nameOf(t)}</b> },
    { key: 'kind', header: tBoards('boards.v1.settings.share.col_kind'), render: kindBadge },
    { key: 'count', header: tBoards('boards.v1.settings.share.col_count'), numeric: true, render: () => '—' },
    {
      key: 'notify', header: tBoards('boards.v1.settings.share.col_notify'), align: 'center',
      render: (t) => <Checkbox checked={t.canNotify ?? false} disabled={!form.notifyEnabled} aria-label={tBoards('boards.v1.settings.share.notify_aria', { name: nameOf(t) })} onChange={(e) => patchShare(t, { canNotify: e.target.checked })} />,
    },
  ];

  const managerColumns: TableColumn<BoardMemberDto>[] = [
    {
      key: 'person', header: tBoards('boards.v1.settings.managers.col_person'),
      render: (m) => <span className="bv1-who">{m.name ?? tBoards('boards.v1.settings.share.unknown')}{m.email && <small>{m.email}</small>}</span>,
    },
    { key: 'role', header: tBoards('boards.v1.settings.managers.col_role'), render: (m) => <span className={`bv1-kbadge${m.role === 'OWNER' ? ' is-pur' : ''}`}>{m.role}</span> },
    {
      key: 'remove', header: <span className="bv1-sr">{tBoards('boards.v1.settings.share.col_remove')}</span>, align: 'right', width: '56px',
      render: (m) => (m.role === 'OWNER' || m.userId === board.createdBy
        ? <span title={tBoards('boards.v1.settings.managers.owner_locked')} aria-label={tBoards('boards.v1.settings.managers.owner_locked')}>—</span>
        : (
          <Button variant="ghost" size="sm" iconOnly disabled={busy} aria-label={tBoards('boards.v1.settings.managers.remove', { name: m.name ?? m.userId })} onClick={() => void removeManager(m)}>
            <X aria-hidden />
          </Button>
        )),
    },
  ];

  const tabs = [
    { value: 'basic', label: tBoards('boards.v1.settings.tab.basic') },
    { value: 'share', label: tBoards('boards.v1.settings.tab.share') },
    { value: 'post', label: tBoards('boards.v1.settings.tab.post') },
    { value: 'notify', label: tBoards('boards.v1.settings.tab.notify') },
  ];
  const note = changed.length > 0
    ? `${tBoards('boards.v1.settings.bar.changed', {
      n: changed.length,
      items: changed.map((k) => tBoards(`boards.v1.settings.field.${k}` as BoardsI18nKey)).join(' · '),
    })}${sharesDirty ? tBoards('boards.v1.settings.bar.shares_whole') : ''}`
    : tBoards('boards.v1.settings.bar.none');

  return (
    <>
      <PageHeader
        className="bv1-head"
        breadcrumb={[tBoards('boards.v1.crumb'), board.name, tBoards('boards.v1.settings.title')]}
        title={tBoards('boards.v1.settings.title')}
        actions={<Button variant="secondary" onClick={() => navigate(boardListPath(board.id))}>{tBoards('boards.v1.settings.back')}</Button>}
      />

      <section className="bv1-card bv1-set" aria-label={tBoards('boards.v1.settings.title')}>
        <header className="bv1-set__head">
          <span className="bv1-set__tile" aria-hidden><Settings /></span>
          <div className="bv1-set__main">
            <div className="bv1-set__name">
              <h2>{board.name}</h2>
              {board.systemKey && <BoardMark kind="default-board" />}
              <span className="bv1-kbadge">{tBoards(board.boardType === 'FEED' ? 'boards.v1.settings.badge_feed' : 'boards.v1.settings.badge_classic')}</span>
              <span className={`bv1-kbadge${board.notifyEnabled ? ' is-ok' : ''}`}>
                {tBoards(board.notifyEnabled ? 'boards.v1.settings.badge_notify_on' : 'boards.v1.settings.badge_notify_off')}
              </span>
            </div>
            {board.description && <p className="bv1-set__desc">{board.description}</p>}
            <p className="bv1-set__meta bv1-num">
              {tBoards('boards.v1.settings.meta', { shares: detail.shares.length === shares.length ? shares.length : shares.length, managers: members.length, prefixes: prefixes.length })}
            </p>
          </div>
          <div className="bv1-set__fill">
            <span>{tBoards('boards.v1.settings.fill_label')}</span>
            <b>{tBoards('boards.v1.settings.fill_none')}</b>
          </div>
        </header>

        <div className="bv1-set__tabbar">
          <Tabs variant="pill" ariaLabel={tBoards('boards.v1.settings.tabs_aria')} items={tabs} value={tab} onChange={setTab} />
          {changed.length > 0 && <span className="bv1-kbadge is-warn">{tBoards('boards.wide.changed', { n: changed.length })}</span>}
        </div>

        <div className="bv1-set__pane" role="tabpanel" aria-label={tabs.find((t) => t.value === tab)?.label}>
          {error && <p className="bd-error bv1-write__warn" role="alert">{error}</p>}

          {tab === 'basic' && (
            <>
              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.basic.sec')}</h3>
                <div className="bv1-fgrid">
                  <FormField label={tBoards('boards.v1.settings.basic.name')} required>
                    <Input fullWidth value={form.name} maxLength={BOARD_NAME_MAX} aria-label={tBoards('boards.v1.settings.basic.name')} onChange={(e) => set('name', e.target.value)} />
                  </FormField>
                  <div>
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.basic.type')}</span>
                    <div className="bv1-chips" role="group" aria-label={tBoards('boards.v1.settings.basic.type')}>
                      <FilterChip pressed={form.boardType === 'CLASSIC'} onClick={() => set('boardType', 'CLASSIC')}>{tBoards('boards.v1.settings.basic.type_classic')}</FilterChip>
                      <FilterChip pressed={form.boardType === 'FEED'} onClick={() => set('boardType', 'FEED')}>{tBoards('boards.v1.settings.basic.type_feed')}</FilterChip>
                    </div>
                  </div>
                  <div className="bv1-fgrid__wide">
                    <FormField label={tBoards('boards.v1.settings.basic.desc')} hint={tBoards('boards.v1.settings.basic.desc_hint')}>
                      <Textarea fullWidth value={form.description} maxLength={BOARD_DESC_MAX} aria-label={tBoards('boards.v1.settings.basic.desc')} onChange={(e) => set('description', e.target.value)} />
                    </FormField>
                  </div>
                </div>
              </div>
              {board.systemKey ? (
                <div className="bv1-dzone is-locked">
                  <div><b>{tBoards('boards.v1.settings.basic.locked_title')}</b><span>{tBoards('boards.v1.settings.basic.locked_desc')}</span></div>
                </div>
              ) : (
                <div className="bv1-dzone is-locked">
                  <div><b>{tBoards('boards.v1.settings.basic.hold_title')}</b><span>{tBoards('boards.v1.settings.basic.hold_desc')}</span></div>
                </div>
              )}
            </>
          )}

          {tab === 'share' && (
            <>
              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.share.public')}</h3>
                <div className="bv1-optlist">
                  <OptRow title={tBoards('boards.v1.settings.share.private')} hint={tBoards('boards.v1.settings.share.private_hint')}>
                    <Switch checked={form.isPrivate} onCheckedChange={(v) => set('isPrivate', v)} aria-label={tBoards('boards.v1.settings.share.private')} />
                  </OptRow>
                </div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.share.targets')}</h3>
                <div className="bv1-addline">
                  <div className="bv1-addline__field">
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.share.kind')}</span>
                    <Combobox
                      value={kind}
                      onChange={(v) => { setKind(v as ShareTargetType); setPick(''); }}
                      options={(['company', 'division', 'team', 'user'] as const).map((k) => ({ value: k, label: tBoards(KIND_KEY[k]) }))}
                      ariaLabel={tBoards('boards.v1.settings.share.kind')}
                    />
                  </div>
                  {kind === 'user' && (
                    <div className="bv1-addline__field">
                      <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.managers.search')}</span>
                      <Input fullWidth value={shareQuery} placeholder={tBoards('boards.v1.settings.share.user_search')} aria-label={tBoards('boards.v1.settings.managers.search')} onChange={(e) => setShareQuery(e.target.value)} />
                    </div>
                  )}
                  {kind !== 'company' && (
                    <div className="bv1-addline__field">
                      <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.share.target')}</span>
                      <Combobox value={pick} onChange={setPick} options={pickOptions} searchable ariaLabel={tBoards('boards.v1.settings.share.target')} placeholder={tBoards('boards.v1.settings.managers.pick')} />
                    </div>
                  )}
                  <Button variant="primary" iconLeft={<Plus aria-hidden />} disabled={kind !== 'company' && !pick} onClick={addShare}>{tBoards('boards.btn.add')}</Button>
                </div>
                {shares.length > 0 ? (
                  <Table caption={tBoards('boards.v1.settings.share.targets')} columns={shareColumns} rows={shares} rowKey={shareKey} wrapClassName="bv1-tablewrap" />
                ) : (
                  <p className="bv1-emptybox">{tBoards('boards.v1.settings.share.empty')}</p>
                )}
                <div className="bv1-listfoot bv1-num">
                  {tBoards('boards.v1.settings.share.foot', { n: shares.length })}{!form.allowPostWrite && tBoards('boards.v1.settings.share.foot_write')}
                </div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.managers.sec')}</h3>
                <div className="bv1-addline">
                  <div className="bv1-addline__field">
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.managers.search')}</span>
                    <Input fullWidth value={managerQuery} placeholder={tBoards('boards.v1.settings.share.user_search')} aria-label={tBoards('boards.v1.settings.managers.search')} onChange={(e) => setManagerQuery(e.target.value)} />
                  </div>
                  <div className="bv1-addline__field">
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.managers.pick')}</span>
                    <Combobox value={managerPick} onChange={setManagerPick} options={managerUsers.map((u) => ({ value: u.id, label: u.name }))} searchable ariaLabel={tBoards('boards.v1.settings.managers.pick')} placeholder={tBoards('boards.v1.settings.managers.pick')} />
                  </div>
                  <Button variant="secondary" iconLeft={<Plus aria-hidden />} disabled={!managerPick || busy} onClick={() => void addManager()}>{tBoards('boards.btn.add')}</Button>
                </div>
                <Table caption={tBoards('boards.v1.settings.managers.sec')} columns={managerColumns} rows={members} rowKey={(m) => m.userId} wrapClassName="bv1-tablewrap" />
                <p className="bv1-note">{tBoards('boards.v1.settings.post.instant_note')}</p>
              </div>
            </>
          )}

          {tab === 'post' && (
            <>
              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.post.sec')}</h3>
                <div className="bv1-optlist">
                  <OptRow title={tBoards('boards.settings.allow_post_write')} hint={tBoards('boards.v1.settings.post.write_hint')}>
                    <Switch checked={form.allowPostWrite} onCheckedChange={(v) => set('allowPostWrite', v)} aria-label={tBoards('boards.settings.allow_post_write')} />
                  </OptRow>
                  <OptRow title={tBoards('boards.settings.allow_anonymous')} hint={tBoards('boards.v1.settings.post.anonymous_hint')}>
                    <Switch checked={form.allowAnonymous} onCheckedChange={(v) => set('allowAnonymous', v)} aria-label={tBoards('boards.settings.allow_anonymous')} />
                  </OptRow>
                  <OptRow title={tBoards('boards.settings.allow_comments')} hint={tBoards('boards.v1.settings.post.comments_hint')}>
                    <Switch checked={form.allowComments} onCheckedChange={(v) => set('allowComments', v)} aria-label={tBoards('boards.settings.allow_comments')} />
                  </OptRow>
                  <OptRow title={tBoards('boards.v1.settings.post.mail')} hint={tBoards('boards.v1.settings.post.mail_hint')}>
                    <Switch checked={form.showMailButton} onCheckedChange={(v) => set('showMailButton', v)} aria-label={tBoards('boards.v1.settings.post.mail')} />
                  </OptRow>
                  <OptRow title={tBoards('boards.settings.show_secret_titles')} hint={tBoards('boards.v1.settings.post.secret_titles_hint')}>
                    <Switch checked={form.showSecretTitles} onCheckedChange={(v) => set('showSecretTitles', v)} aria-label={tBoards('boards.settings.show_secret_titles')} />
                  </OptRow>
                  <OptRow title={tBoards('boards.settings.use_post_readers')} hint={tBoards('boards.v1.settings.post.readers_hint')}>
                    <Switch checked={form.usePostReaders} onCheckedChange={(v) => set('usePostReaders', v)} aria-label={tBoards('boards.settings.use_post_readers')} />
                  </OptRow>
                </div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.post.prefixes')}</h3>
                <div className="bv1-prefixes">
                  {prefixes.map((p) => (
                    <span key={p.id} className="bv1-tchip">
                      {p.label}
                      <Button variant="ghost" size="sm" iconOnly disabled={busy} aria-label={tBoards('boards.v1.settings.share.remove', { name: p.label })} onClick={() => void removePrefix(p)}>
                        <X aria-hidden />
                      </Button>
                    </span>
                  ))}
                  <span className="bv1-prefixes__add">
                    <Input
                      value={newPrefix}
                      maxLength={PREFIX_LABEL_MAX}
                      placeholder={tBoards('boards.v1.settings.post.prefix_ph')}
                      aria-label={tBoards('boards.v1.settings.post.prefix_ph')}
                      onChange={(e) => setNewPrefix(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addPrefix(); } }}
                    />
                    <Button variant="secondary" size="sm" disabled={!newPrefix.trim() || busy} onClick={() => void addPrefix()}>{tBoards('boards.btn.add')}</Button>
                  </span>
                </div>
                <p className="bv1-note">{tBoards('boards.v1.settings.post.instant_note')}</p>
              </div>
            </>
          )}

          {tab === 'notify' && (
            <>
              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.notify.sec')}</h3>
                <div className="bv1-optlist">
                  <OptRow
                    title={tBoards('boards.settings.notify_enabled')}
                    hint={tBoards(form.notifyEnabled ? 'boards.v1.settings.notify.enabled_on' : 'boards.v1.settings.notify.enabled_off')}
                  >
                    <Switch checked={form.notifyEnabled} onCheckedChange={(v) => set('notifyEnabled', v)} aria-label={tBoards('boards.settings.notify_enabled')} />
                  </OptRow>
                  <OptRow
                    title={tBoards('boards.settings.notify_all_default')}
                    hint={tBoards(form.notifyEnabled ? 'boards.v1.settings.notify.all_hint' : 'boards.v1.settings.notify.all_off')}
                    off={!form.notifyEnabled}
                  >
                    <Switch
                      checked={form.notifyAllByDefault}
                      disabled={!form.notifyEnabled}
                      onCheckedChange={(v) => set('notifyAllByDefault', v)}
                      aria-label={tBoards('boards.settings.notify_all_default')}
                    />
                  </OptRow>
                </div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.notify.targets')}</h3>
                <p className="bv1-set__desc-line">{tBoards('boards.v1.settings.notify.desc')}</p>
                {!form.notifyEnabled && (
                  <p className="bv1-lockline"><Lock aria-hidden />{tBoards('boards.v1.settings.notify.locked')}</p>
                )}
                {shares.length > 0 ? (
                  <div className={form.notifyEnabled ? undefined : 'bv1-is-locked'}>
                    <Table caption={tBoards('boards.v1.settings.notify.targets')} columns={notifyColumns} rows={shares} rowKey={shareKey} wrapClassName="bv1-tablewrap" />
                  </div>
                ) : (
                  <p className="bv1-emptybox">{tBoards('boards.v1.settings.notify.empty')}</p>
                )}
                <div className="bv1-listfoot bv1-num">{tBoards('boards.v1.settings.notify.foot', { n: notifyTargetCount(shares) })}</div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.notify.how')}</h3>
                <div className="bv1-fgrid">
                  <div>
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.notify.preview')}</span>
                    <p className="bv1-emptybox">{tBoards('boards.v1.settings.notify.preview_none')}</p>
                  </div>
                  <div>
                    <span className="bv1-fgrid__label">{tBoards('boards.v1.settings.notify.rules')}</span>
                    <ol className="bv1-flow">
                      <li><span className="bv1-flow__dot" aria-hidden /><div><b>{tBoards('boards.v1.settings.notify.rule_new')}</b><span>{tBoards('boards.v1.settings.notify.rule_new_desc')}</span></div></li>
                      <li><span className="bv1-flow__dot" aria-hidden /><div><b>{tBoards('boards.v1.settings.notify.rule_edit')}</b><span>{tBoards('boards.v1.settings.notify.rule_edit_desc')}</span></div></li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="bv1-fsec">
                <h3 className="bv1-fsec__h3">{tBoards('boards.v1.settings.notify.recent')}</h3>
                <p className="bv1-emptybox">{tBoards('boards.v1.settings.notify.recent_none')}</p>
              </div>
            </>
          )}
        </div>

        <div className="bv1-set__bar">
          <span className="bv1-set__note">{note}</span>
          <Button variant="secondary" size="sm" disabled={saving} onClick={() => navigate(boardListPath(board.id))}>{tBoards('boards.btn.cancel')}</Button>
          <Button variant="primary" size="sm" disabled={!canSave} onClick={() => void save()}>{tBoards('boards.btn.save')}</Button>
        </div>
      </section>

      {/* D-U5-1 — 탭은 주소(?tab=)라 탭을 누를 때마다 이동이 일어난다. 같은 경로 안의 이동은 통과시키고 나가는 것만 묻는다. */}
      <UnsavedGuard
        ignoreSamePath
        when={changed.length > 0 && !saving}
        title={tBoards('boards.v1.settings.discard.title')}
        description={tBoards('boards.v1.settings.discard.desc')}
        stayLabel={tBoards('boards.v1.settings.discard.stay')}
        leaveLabel={tBoards('boards.v1.settings.discard.leave')}
      />
    </>
  );
}
